import { GeoJsonLayer } from '@deck.gl/layers'
import { useQuery } from '@tanstack/react-query'
import centerOfMass from '@turf/center-of-mass'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import type { Feature, FeatureCollection } from 'geojson'
import { useMemo, useState } from 'react'
import { aVisibleGeographies, useAtomValue } from '~/atoms'
import { isMapLayerGeographyType } from '~/domains/geographies/predicates'
import { MapView, useCurrentMapView } from '~/hooks'
import { useTRPC } from '~/trpc/client'
import { GOOGLE_DECK_LAYER_ORDER, useGoogleDeckLayerGroup } from '../google'

export const GOOGLE_GEOGRAPHIES_FILL_LAYER_ID = 'google-map-geographies-fill'

type GeographyFeatureProperties = {
  _id: string
  latitude: number
  longitude: number
  name: string
  type: string
}

function getGeographyFeature(object: unknown) {
  const feature = object as Feature | undefined

  if (!feature?.geometry) return null

  return feature as Feature<Feature['geometry'], GeographyFeatureProperties>
}

export function GoogleGeographiesLayer() {
  const trpc = useTRPC()
  const currentMapView = useCurrentMapView()
  const visibleGeographies = useAtomValue(aVisibleGeographies)
  const visibleMapLayerGeographies = useMemo(
    () => visibleGeographies.filter(isMapLayerGeographyType),
    [visibleGeographies]
  )
  const showGeographies = visibleMapLayerGeographies.length > 0
  const [selectedGeography, setSelectedGeography] =
    useState<GeographyFeatureProperties | null>(null)

  const geographiesQuery = useQuery(
    trpc.geographies.mapLayer.queryOptions(
      {
        types: visibleMapLayerGeographies,
      },
      {
        enabled: showGeographies,
      }
    )
  )
  const geographies = showGeographies ? (geographiesQuery.data ?? []) : []

  const geojson = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: geographies.map((geo) => {
        const center = centerOfMass(geo.feature.geometry)

        return {
          ...geo.feature,
          id: geo._id,
          properties: {
            _id: geo._id,
            latitude: center.geometry.coordinates[1],
            longitude: center.geometry.coordinates[0],
            name: geo.name,
            type: geo.type,
          },
        }
      }),
    } as FeatureCollection
  }, [geographies])

  const layers = useMemo(
    () =>
      showGeographies && geographies.length > 0 && currentMapView === MapView.Map
        ? [
            new GeoJsonLayer({
              id: GOOGLE_GEOGRAPHIES_FILL_LAYER_ID,
              data: geojson,
              pickable: true,
              stroked: true,
              filled: true,
              lineWidthUnits: 'pixels',
              lineWidthMinPixels: 2,
              getFillColor: [153, 161, 175, 31],
              getLineColor: [82, 82, 92, 255],
              getLineWidth: 2,
            }),
          ]
        : [],
    [currentMapView, geographies.length, geojson, showGeographies]
  )

  useGoogleDeckLayerGroup({
    id: 'google-map-geographies',
    layers,
    order: GOOGLE_DECK_LAYER_ORDER.geographies,
    hoverPickRadius: 4,
    onHover: (result) => {
      const feature =
        result?.layerId === GOOGLE_GEOGRAPHIES_FILL_LAYER_ID
          ? getGeographyFeature(result.object)
          : null

      setSelectedGeography(feature?.properties ?? null)
    },
  })

  if (!showGeographies || geographies.length === 0 || currentMapView !== MapView.Map) {
    return null
  }

  return (
    <>
      {selectedGeography && (
        <AdvancedMarker
          position={{
            lat: selectedGeography.latitude,
            lng: selectedGeography.longitude,
          }}
          anchorLeft="-50%"
          anchorTop="calc(-100% - 10px)"
          zIndex={45}>
          <div className="pointer-events-none w-[160px] max-w-[calc(100vw-3rem)] rounded-md bg-background px-4 py-3 shadow-md">
            <p className="line-clamp-1 text-sm">{selectedGeography.name}</p>
          </div>
          <div className="absolute left-1/2 top-full h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-background" />
        </AdvancedMarker>
      )}
    </>
  )
}
